<template>
	<Modal v-model:visible="visible" :title="$t('paas.reviewModal.header')" width="95vw" @ok="onOk" @cancel="onCancel">
		<VerticalLayout>
			<div class="scrollable-x">
				<Table :columns="columns" :data-source="comparisonsLocal" :pagination="false" size="small" :loading="isLoading">
					<template #bodyCell="{ column, record }">
						<template v-if="column.dataIndex === 'status'">
							<LabeledSwitch
								v-if="record.status === 'found'"
								small
								v-model="record.store"
								:rightLabel="$t('paas.reviewModal.statuses.' + record.status)"
							/>
							<template v-else-if="['failed', 'cancelled', 'deleted'].includes(record.status)">
								{{ $t('paas.reviewModal.statuses.' + record.status) }}
							</template>
							<template v-else>
								{{ $t('paas.reviewModal.statuses.not_found') }}
							</template>
						</template>
						<template v-else-if="column.dataIndex.includes('Diff')">
							<PaasCoordsDiff
								:fromValue="record[column.dataIndex].fromValue"
								:toValue="record[column.dataIndex].toValue"
							/>
						</template>
						<template v-else-if="column.dataIndex === 'antArp'">
							<VerticalLayout size="small">
								<!-- в этом столбце много места, так что выведем здесь ошибку -->
								<TableErrorMessagesDisplay
									v-if="record.status === 'failed' || record.status === 'cancelled'"
									errorKey="paas.reviewModal.errorMessages.errorHeader"
									errorLevel="error"
									:messages="record.errorMessages"
								/>
								<div v-else>
									{{ record.antReferencePoint }}
									<DetectableFieldDisplay
										:dbValue="record.antReferencePoint"
										:compareValue="record.antArp"
										nullValue="NA"
										displayValuePrefix="base-stations-edit.fields.antReferencePoint.options"
										compareAlertKey="base-stations-edit.fields.antReferencePoint.conflict.paasMessage"
										dbLabelKey="base-stations-edit.fields.antReferencePoint.conflict.databaseValue"
										compareLabelKey="base-stations-edit.fields.antReferencePoint.conflict.paasValue"
									/>
								</div>
								<TableErrorMessagesDisplay
									v-if="record.warningMessages.length > 0"
									errorKey="paas.reviewModal.errorMessages.warningHeader"
									errorLevel="warning"
									:messages="record.warningMessages"
								/>
							</VerticalLayout>
						</template>
					</template>
				</Table>
			</div>

			<LabeledSwitch
				v-model="selectAllSwitchValue"
				class="pd-md-top"
				:rightLabel="$t('paas.reviewModal.selectAllSwitch')"
				@change="toggleSelectAll"
			/>
		</VerticalLayout>

		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('paas.reviewModal.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" @click="onOk">{{ $t('paas.reviewModal.okButtonLabel') }}</Button>
		</template>
	</Modal>
</template>

<script>
import { ref, toRaw, computed, toRef, watch } from 'vue';
import { Modal, Button, Table, message } from 'ant-design-vue';

import ApiPaas from '@/api/ApiPaas';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';

import { useI18n } from 'vue-i18n'

import { useBaseStationsStore } from '@/stores/baseStations';
import { showErrorMessage } from '@/components/common/errorMessage';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import DetectableFieldDisplay from '@/components/baseStations/DetectableFieldDisplay';
import TableErrorMessagesDisplay from '@/components/baseStations/TableErrorMessagesDisplay';
import VerticalLayout from '@/components/common/VerticalLayout';

import PaasCoordsDiff from '@/components/paas/PaasCoordsDiff';


const getDiff = (item, measure) => {
	if (item.coords == null) {
		return {
			fromValue: null,
			toValue: null,
		};
	}
	return {
		fromValue: item['base_station'][measure + '_coord'],
		toValue: item.coords[measure + '_coord'],
	};
}

const getDistance = item => {
	if (item.coords == null) {
		return '';
	}
	if (
		item['base_station']['x_coord'] == null ||
		item['base_station']['y_coord'] == null ||
		item['base_station']['z_coord'] == null
	) {
		return '—';
	}
	return Utils.formatMetres(Utils.mm2m(Math.sqrt(
		Math.pow(item['base_station']['x_coord'] - item['coords']['x_coord'], 2) +
			Math.pow(item['base_station']['y_coord'] - item['coords']['y_coord'], 2) +
			Math.pow(item['base_station']['z_coord'] - item['coords']['z_coord'], 2)
	)));
}


export default {
	components: {
		Modal, Button, Table,
		LabeledSwitch, DetectableFieldDisplay, TableErrorMessagesDisplay, PaasCoordsDiff, VerticalLayout,
	},
	emits: ['update:modelValue', 'applied'],
	props: ['modelValue', 'taskId'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const isLoading = ref(true);
		const isSubmitting = ref(false);
		const comparisonsLocal = ref([]);
		const visibleRef = toRef(props, 'modelValue');
		const visible = computed({
			get() {
				return visibleRef.value;
			},
			set(value) {
				emit('update:modelValue', value);
			}
		});

		const selectAllSwitchValue = ref(true);

		const paasTaskReviewHandler = response => {
			if (response.status === 'ok') {
				let comparisons = response.comparisons.map(item => ({
					...item,
					id: item.coords?.id,
					mntpntInt: item['base_station']['mntpnt_int'],
					label: item['base_station'].label,
					antReferencePoint: item['base_station']['ant_reference_point'],
					antArp: item.coords?.ant_arp,
					antDescription: item.coords?.ant_descr,
					store: item.status === 'found',
					xDiff: getDiff(item, 'x'),
					yDiff: getDiff(item, 'y'),
					zDiff: getDiff(item, 'z'),
					distance: getDistance(item),
				}));
				comparisons.sort(Utils.objectFieldMultiSorter('status'));
				comparisonsLocal.value = comparisons.map(item => {
					let errorMessages = [];
					if (item.status === 'failed' || item.status === 'cancelled') {
						errorMessages.push({
							key: t('paas.reviewModal.errorMessages.errorKey'),
							message: item.message,
						});
						if (item.task_uid) {
							errorMessages.push({
								key: t('paas.reviewModal.errorMessages.taskUidKey'),
								message: item.task_uid,
							});
						}
					}
					const warningMessages = item.warnings.map(warning => ({
						key: warning.filename,
						message: warning.message,
					}));
					// errorMessages -- ошибки из системы web
					// warningMessages -- ошибки из системы thinner или paas
					return { ...item, errorMessages, warningMessages };
				});
			} else {
				showErrorMessage(response.errors);
			}
			isLoading.value = false;
		};

		const toggleSelectAll = () => {
			comparisonsLocal.value.forEach(record => {
				if (record.status === 'found') {
					record.store = selectAllSwitchValue.value;
				}
			});
		};

		const onOk = () => {
			isSubmitting.value = true;
			const toUpdate = toRaw(comparisonsLocal.value.filter(item => item.store).map(item => item.id));
			ApiPaas.paasTaskApply(toUpdate, response => {
				if (response.status === 'ok') {
					message.success(t('paas.reviewModal.success'));
					useBaseStationsStore().baseStationRefreshList();
					visible.value = false;
					emit('applied');
				} else {
					showErrorMessage(response.errors);
				}
				isSubmitting.value = false;
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		watch(() => props.taskId, (value) => {
			isLoading.value = true;
			ApiPaas.paasTaskReview(value, paasTaskReviewHandler);
		});

		return {
			isLoading,
			isSubmitting,
			selectAllSwitchValue,
			comparisonsLocal,
			columns: Consts.paasReviewTableColumnsComp,
			visible,
			toggleSelectAll,
			onOk,
			onCancel,
		};
	},
}
</script>
